import { useEffect, FC } from "react";
import { Outlet, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  getUserProfileCardSidebarAsync,
  getUserProfileInfoAsync,
} from "src/redux/users/actions";
import {
  selectorUserProfileCard,
  selectorUserProfileInfo,
} from "src/redux/users/selectors";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { ProfileSidebar } from "src/page-components/users/user-profile/ProfileSidebar";
import {
  metaTrns,
  rolesTrns,
} from "src/page-components/users/user-profile/trns";
import { MetadataWrapper } from "src/components/MetadataWrapper";
import { Loader } from "src/components/Loader";
import { Roles } from "src/@types/users";
import { Sizes } from "src/@types/sizes";
import { useExtractId } from "src/hooks/useExractId";

const UserProfile: FC = () => {
  const { t } = useTranslation();
  const { id: idWithSlug } = useParams();
  const id = useExtractId("id");

  const dispatch = useAppDispatch();

  const userProfileInfo = useAppSelector(selectorUserProfileInfo);

  const userProfileCard = useAppSelector(selectorUserProfileCard);

  useEffect(() => {
    dispatch(getUserProfileInfoAsync(id));
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(getUserProfileCardSidebarAsync(id));
  }, [dispatch, id]);

  const { role } = userProfileCard?.data?.host || {};

  const title = role === Roles.OWNER ? t(rolesTrns.owner) : t(rolesTrns.agent);

  const isLoading = userProfileInfo.isLoading || userProfileCard.isLoading;

  const metaName = `${userProfileInfo?.data?.host?.first_name} ${userProfileInfo?.data?.host?.last_name}`;
  const metaDescription = userProfileInfo?.data?.description;

  return (
    <MetadataWrapper
      title={t(metaTrns.title, {
        name: metaName,
      })}
      description={t(metaTrns.description, {
        name: metaName,
        description: metaDescription,
      })}
    >
      <PageWrapper mainClassName="pt-20 pb-5 gl:py-35">
        <section className="container">
          {isLoading ? (
            <Loader size={Sizes.L} />
          ) : (
            <>
              <div className="mb-2 lg:mb-10">
                <h1>{title}</h1>
              </div>

              <div className="flex flex-col gl:flex-row items-center gl:items-start gap-5 mt-2 gl:mt-3">
                <ProfileSidebar
                  idWithSlug={idWithSlug}
                  userProfileCard={userProfileCard?.data}
                />
                <Outlet />
              </div>
            </>
          )}
        </section>
      </PageWrapper>
    </MetadataWrapper>
  );
};

export default UserProfile;
